import { useState } from "react";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "rw", label: "Kinyarwanda" },
  { code: "fr", label: "Français" },
];

// Same wording as the mobile app's `guidelines` translations.
const CONTENT = {
  en: {
    intro: "The essentials for every business taking bookings on SHUWA.",
    items: [
      ["Your payment is credited to your SHUWA business balance after the service is completed.", "Once the payment becomes available, you can withdraw your available balance according to SHUWA's payout rules."],
      ["You control your cancellation policy.", "You can set the cancellation period and applicable cancellation fee for your services. These conditions are shown to customers before they confirm a booking."],
      ["Keep your availability up to date.", "Make sure your working hours, services, staff schedules and unavailable times are accurate to avoid double bookings."],
      ["A confirmed booking is your responsibility to honor.", "Once a customer books an available slot, you should be prepared to provide the service at the confirmed date and time."],
      ["You can manage your bookings directly through SHUWA.", "Accept, view, reschedule or cancel bookings from your business account according to your settings and SHUWA's policies."],
      ["Keep your prices and services accurate.", "Customers should see the correct service, price and duration before making a booking."],
      ["Communicate changes early.", "If you cannot provide a confirmed appointment, cancel or reschedule it as soon as possible and notify the customer."],
      ["Repeated no-shows or cancellations can affect your business account.", "SHUWA may take action where a provider repeatedly fails to honor confirmed appointments."],
      ["Customer payments and your earnings are separate from SHUWA's booking fee.", "The customer may see a booking/service fee when booking through SHUWA, while your business earnings are recorded in your SHUWA balance according to the applicable fee structure."],
    ],
  },
  fr: {
    intro: "L'essentiel pour toute entreprise qui reçoit des réservations sur SHUWA.",
    items: [
      ["Votre paiement est crédité sur votre solde professionnel SHUWA une fois la prestation réalisée.", "Dès que le paiement est disponible, vous pouvez retirer votre solde disponible conformément aux règles de versement de SHUWA."],
      ["Vous définissez votre politique d'annulation.", "Vous pouvez fixer le délai d'annulation et les frais d'annulation applicables à vos services. Ces conditions sont présentées aux clients avant qu'ils ne confirment une réservation."],
      ["Tenez vos disponibilités à jour.", "Vérifiez que vos horaires d'ouverture, vos services, les plannings de votre équipe et vos indisponibilités sont exacts afin d'éviter les doubles réservations."],
      ["Il vous appartient d'honorer toute réservation confirmée.", "Dès qu'un client réserve un créneau disponible, vous devez être prêt à assurer la prestation à la date et à l'heure confirmées."],
      ["Vous gérez vos réservations directement dans SHUWA.", "Acceptez, consultez, reportez ou annulez des réservations depuis votre compte professionnel, selon vos paramètres et les règles de SHUWA."],
      ["Gardez vos prix et vos services exacts.", "Les clients doivent voir le bon service, le bon prix et la bonne durée avant de réserver."],
      ["Prévenez tôt en cas de changement.", "Si vous ne pouvez pas assurer un rendez-vous confirmé, annulez-le ou reportez-le dès que possible et prévenez le client."],
      ["Les absences ou annulations répétées peuvent affecter votre compte professionnel.", "SHUWA peut prendre des mesures lorsqu'un prestataire manque de façon répétée à ses rendez-vous confirmés."],
      ["Les paiements des clients et vos revenus sont distincts des frais de réservation SHUWA.", "Le client peut voir des frais de réservation ou de service lorsqu'il réserve via SHUWA, tandis que vos revenus sont enregistrés dans votre solde SHUWA selon la grille tarifaire applicable."],
    ],
  },
  rw: {
    intro: "Iby'ingenzi kuri buri bucuruzi bwakira gahunda kuri SHUWA.",
    items: [
      ["Amafaranga wishyuwe ashyirwa kuri konti yawe y'ubucuruzi ya SHUWA nyuma y'uko serivisi itanzwe.", "Iyo amafaranga amaze kuboneka, ushobora kubikuza ayo ufite hakurikijwe amategeko ya SHUWA agenga kwishyurwa."],
      ["Ni wowe ugena politiki yawe yo guhagarika gahunda.", "Ushobora kugena igihe cyo guhagarika gahunda n'amafaranga yo guhagarika acibwa kuri serivisi zawe. Ibi byerekwa abakiriya mbere y'uko bemeza gahunda."],
      ["Jya uhuza n'igihe amasaha ubonekamo.", "Reba ko amasaha y'akazi, serivisi, ingengabihe z'abakozi n'ibihe utabonekamo ari ukuri, kugira ngo wirinde ko gahunda ebyiri zihurirana."],
      ["Kubahiriza gahunda yemejwe ni inshingano yawe.", "Iyo umukiriya afashe umwanya uhari, ugomba kuba witeguye gutanga serivisi ku itariki no ku isaha byemejwe."],
      ["Ushobora gucunga gahunda zawe muri SHUWA.", "Emera, reba, wimure cyangwa uhagarike gahunda uhereye kuri konti yawe y'ubucuruzi, hakurikijwe igenamiterere ryawe n'amategeko ya SHUWA."],
      ["Ibiciro na serivisi byawe bigomba kuba ari ukuri.", "Abakiriya bagomba kubona serivisi, igiciro n'igihe imara nyabyo mbere yo gufata gahunda."],
      ["Menyesha impinduka hakiri kare.", "Niba udashobora gutanga serivisi kuri gahunda yemejwe, yihagarike cyangwa uyimure vuba bishoboka kandi ubimenyeshe umukiriya."],
      ["Kutaboneka cyangwa guhagarika gahunda kenshi bishobora kugira ingaruka kuri konti yawe y'ubucuruzi.", "SHUWA ishobora gufata ingamba iyo utanga serivisi ananiwe kenshi kubahiriza gahunda zemejwe."],
      ["Ibyo abakiriya bishyura n'ibyo winjiza bitandukanye n'amafaranga ya SHUWA yo gufata gahunda.", "Umukiriya ashobora kubona amafaranga yo gufata gahunda cyangwa ya serivisi iyo afashe gahunda kuri SHUWA, naho ibyo winjiza byandikwa kuri konti yawe ya SHUWA hakurikijwe ibiciro bikurikizwa."],
    ],
  },
};

export default function BusinessGuidelines() {
  const [language, setLanguage] = useState("en");
  const { intro, items } = CONTENT[language];

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-6">
        {LANGUAGES.map(({ code, label }) => (
          <button
            key={code}
            type="button"
            onClick={() => setLanguage(code)}
            aria-pressed={language === code}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
              language === code ? "bg-primary text-white" : "bg-primary/10 text-primary hover:bg-primary/20"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <p lang={language}>{intro}</p>

      <ul lang={language} className="list-disc space-y-4 pl-5 marker:text-primary">
        {items.map(([title, body]) => (
          <li key={title}>
            <span className="font-semibold text-slate-900">{title}</span> {body}
          </li>
        ))}
      </ul>
    </>
  );
}
